import { useEffect, useState } from 'react';
import { Form, message, Modal } from 'antd';
import { useQuery } from '@tanstack/react-query';
import SearchForm from './components/SearchForm';
import OrderTable from './components/OrderTable';
import OrderDetail from './components/OrderDetail';
import { OrderListParams, OrderListResponse } from '@/types/order';
import { getOrderList, restoreOrderPoints } from '@/api/order';
import {
  loadProductCategories,
  productCategoriesAtom,
} from '@/atoms/productCategories';
import { useAtom } from 'jotai';

const OrderPage = () => {
  const [form] =
    Form.useForm<Omit<OrderListParams, 'currentPage' | 'pageSize'>>();
  const [messageApi, contextHolder] = message.useMessage();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string>();
  const [currentOrderItem, setCurrentOrderItem] = useState<any>();
  const [_, setCategories] = useAtom(productCategoriesAtom);

  useEffect(() => {
    loadProductCategories(setCategories).catch((err) => {
      console.error('Failed to load product categories:', err);
    });
  }, []);

  const { data, isLoading, refetch } = useQuery<OrderListResponse>({
    queryKey: ['orderList', page],
    queryFn: async () => {
      const params: OrderListParams = {
        ...form.getFieldsValue(),
        currentPage: page,
        pageSize,
      };
      const resp = await getOrderList(params);
      if (resp.status !== 200) {
        return {
          total: 0,
          currentPage: 0,
          pageSize: 0,
          records: [],
        };
      }
      return resp.data;
    },
    refetchOnWindowFocus: false,
  });

  const handleSearch = () => {
    setPage(1);
    void refetch();
  };

  const handleReset = () => {
    form.resetFields();
    setPage(1);
    void refetch();
  }

  const handleDetail = (orderId: string, orderItem?: any) => {
    setCurrentOrderId(orderId);
    setCurrentOrderItem(orderItem);
    setDetailVisible(true);
  };

  const handleRefund = (orderId: string) => {
    Modal.confirm({
      title: '确认退款',
      content: '确定要对此订单进行退款操作吗？',
      onOk: async () => {
        try {
          const resp = await restoreOrderPoints(orderId);
          if (resp.status !== 200) {
            messageApi.error('退款失败：' + resp.message);
            return;
          }
          messageApi.success('退款成功');
          void refetch();
        } catch (error) {
          messageApi.error('退款操作失败，请重试');
        }
      },
    });
  };

  useEffect(() => {
    if (!detailVisible) {
      setCurrentOrderId('');
      setCurrentOrderItem(undefined);
    }
  }, [detailVisible])

  return (
    <div className="order-page-container">
      {contextHolder}
      <SearchForm form={form} onSearch={handleSearch} onReset={handleReset} />

      <OrderTable
        loading={isLoading}
        data={data?.records}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onDetail={handleDetail}
        onRefund={handleRefund}
      />

      {
        detailVisible && (
          <OrderDetail
            orderId={currentOrderId}
            visible={detailVisible}
            onClose={() => setDetailVisible(false)}
            onRefreshList={refetch}
            listItem={currentOrderItem}
          />
        )
      }
    </div>
  );
};

export default OrderPage;
